import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Trash2, MessageCircle, Clock, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describe = (t = {}) => {
  const bits = [];
  if (t.meters) bits.push(`${t.thans} than · ${t.meters} m`);
  if (t.packs) bits.push(`${t.packs} pack${t.packs === 1 ? "" : "s"} · ${t.pieces} pieces`);
  return bits;
};

const replyLink = (enquiry) => {
  // Match the backend's _phone_key: keep the last 10 digits, then add the country code
  const digits = (enquiry.phone || "").replace(/\D/g, "");
  const local = digits.slice(-10);
  const phone = local.length === 10 ? `91${local}` : digits;
  const text = `Hello${enquiry.name ? ` ${enquiry.name}` : ""}, regarding your order ${enquiry.reference} with Mutha Paras & Co.`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
};

export default function OrderEnquiries({ onUnauthorized }) {
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchEnquiries = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        fetch(`${API}/order-enquiries`, { credentials: "include" }),
        fetch(`${API}/order-enquiries/count`, { credentials: "include" }),
      ]);
      if (listRes.status === 401 || countRes.status === 401) {
        onUnauthorized();
        return;
      }
      const data = await listRes.json();
      const list = Array.isArray(data) ? data : [];
      setEnquiries(list);
      const count = await countRes.json().catch(() => ({}));
      setTotal(typeof count.count === "number" ? count.count : list.length);
    } catch (e) {
      console.error("Order enquiries load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const remove = async (id, reference) => {
    if (!window.confirm(`Delete order ${reference}? This cannot be undone.`)) return;
    const res = await fetch(`${API}/order-enquiries/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.status === 401) {
      onUnauthorized();
      return;
    }
    if (res.ok) {
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    }
  };

  return (
    <section className="mb-10" data-testid="order-enquiries-section">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
        <h2
          className="text-lg"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          Order enquiries
        </h2>
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{ backgroundColor: "var(--mp-surface)", color: "var(--mp-muted)" }}
          data-testid="order-enquiries-count"
        >
          {total}
        </span>
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: "var(--mp-muted)" }} data-testid="order-enquiries-loading">
          Loading orders…
        </p>
      ) : enquiries.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--mp-muted)" }} data-testid="order-enquiries-empty">
          No orders yet. They appear here the moment a buyer sends one from the catalogue.
        </p>
      ) : (
        <div className="space-y-3">
          {enquiries.map((e, idx) => (
            <div
              key={e.id}
              className="border rounded-sm p-4"
              style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
              data-testid={`order-enquiry-card-${idx}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid={`order-enquiry-name-${idx}`}
                  >
                    {e.name || "Unnamed buyer"}
                    <span className="ml-2 text-[11px] font-normal" style={{ color: "var(--mp-copper)" }}>
                      {e.reference}
                    </span>
                  </p>
                  <p
                    className="text-xs mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1"
                    style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {e.phone && <span data-testid={`order-enquiry-phone-${idx}`}>{e.phone}</span>}
                    {e.gst_registered === true && (
                      <span className="inline-flex items-center gap-1">
                        <FileText className="w-3 h-3" /> GST {e.gst_number || "registered"}
                      </span>
                    )}
                    {e.gst_registered === false && <span>Unregistered</span>}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(e.timestamp).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {e.phone && (
                    <a
                      href={replyLink(e)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`order-enquiry-reply-${idx}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm"
                      style={{ backgroundColor: "#25D366", color: "#FFFFFF" }}
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Reply
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(e.id, e.reference)}
                    data-testid={`order-enquiry-delete-${idx}`}
                    aria-label="Delete order enquiry"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <ul className="space-y-0.5 mb-2">
                {e.items.map((i, k) => (
                  <li
                    key={`${i.quality_id}-${i.number ?? i.cut_length}-${k}`}
                    className="text-xs"
                    style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {i.quality_name} ·{" "}
                    {i.cut_length
                      ? `cut piece ${i.cut_length} — ${i.thans} pack(s)`
                      : `#${i.number} — ${i.thans} than × ${i.than_meters}m`}
                  </li>
                ))}
              </ul>

              <p className="text-[11px]" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                {describe(e.totals).join("  ·  ")}
              </p>
              {e.notes && (
                <p
                  className="text-xs mt-2 pt-2 border-t"
                  style={{ borderColor: "var(--mp-line)", color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                  data-testid={`order-enquiry-notes-${idx}`}
                >
                  “{e.notes}”
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
